import { useEffect, useState } from 'react';

import { subscribeToLocationChange } from '../../utils/locationChange';

export const MOBILE_ONBOARDING_MAIN_PAGE_PATHS = ['/', '/kupit/', '/snyat/', '/commercial/'] as const;

const FIRST_SESSION_MS = 24 * 60 * 60 * 1000;

export function isAllowedMobileOnboardingPath(pathname: string): boolean {
  return (MOBILE_ONBOARDING_MAIN_PAGE_PATHS as readonly string[]).includes(pathname);
}

function getCurrentPathname(): string | null {
  return typeof window === 'undefined' ? null : window.location.pathname;
}

export function useIsOnboardingDisplayAllowed(isMobile: boolean, firstVisitTimestamp: number | undefined): boolean {
  const [mobilePathname, setMobilePathname] = useState<string | null>(null);
  const [firstSessionCheckTime, setFirstSessionCheckTime] = useState(() => Date.now());
  const isFirstSession =
    isMobile && (firstVisitTimestamp === undefined || firstSessionCheckTime - firstVisitTimestamp < FIRST_SESSION_MS);

  useEffect(() => {
    if (!isMobile || isFirstSession) {
      setMobilePathname(null);

      return;
    }
    const updateLocationState = () => {
      setMobilePathname(getCurrentPathname());
    };
    updateLocationState();

    return subscribeToLocationChange(updateLocationState);
  }, [isFirstSession, isMobile]);

  useEffect(() => {
    if (!isMobile || firstVisitTimestamp === undefined) {
      return;
    }
    const firstSessionEndsAt = firstVisitTimestamp + FIRST_SESSION_MS;
    const firstSessionTimeLeft = firstSessionEndsAt - Date.now();
    if (firstSessionTimeLeft <= 0) {
      setFirstSessionCheckTime(currentTime => (currentTime < firstSessionEndsAt ? Date.now() : currentTime));

      return;
    }
    const timerId = window.setTimeout(() => setFirstSessionCheckTime(Date.now()), firstSessionTimeLeft);

    return () => window.clearTimeout(timerId);
  }, [firstVisitTimestamp, isMobile]);

  const isAllowedPage = mobilePathname !== null && isAllowedMobileOnboardingPath(mobilePathname);

  return !isMobile || (!isFirstSession && isAllowedPage);
}
